import React, { useState, useEffect } from 'react';
import { getCurrentActivity, ACTIVITY_PROFILES, RATIO_TO_PROFILE_MAP } from './StarterUtils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface StarterActivityGraphProps {
  hoursSinceLastFeed: number;
  storage: 'room' | 'fridge';
  roomTemperature?: number;
  feedingRatio?: string;
  className?: string;
}

export function StarterActivityGraph({ 
  hoursSinceLastFeed, 
  storage,
  roomTemperature,
  feedingRatio = '1-1-1',
  className = ''
}: StarterActivityGraphProps) {
  const [visibleBars, setVisibleBars] = useState<boolean[]>([]);
  // Get the correct profile based on the feeding ratio mapping
  const profileKey = RATIO_TO_PROFILE_MAP[feedingRatio] || '1-1-1';
  const profile = ACTIVITY_PROFILES[profileKey];
  const activityPoints = storage === 'room' ? profile.room : profile.fridge;
  const [activityTrend, setActivityTrend] = useState<'up' | 'down' | 'stable'>('stable');
  const points = activityPoints.map((point, index) => ({
    id: `${point.hours}h`,
    ...point,
    label: index === activityPoints.length - 1 ? `${point.hours}h+` : undefined
  }));

  useEffect(() => {
    setVisibleBars(new Array(points.length).fill(false));
    
    points.forEach((_, index) => {
      setTimeout(() => {
        setVisibleBars(prev => {
          const next = [...prev];
          next[index] = true;
          return next;
        });
      }, index * 100);
    });
  }, [storage, profileKey]);

  // Get current activity using the utility function
  const currentActivity = getCurrentActivity(
    hoursSinceLastFeed,
    storage,
    roomTemperature,
    feedingRatio
  );

  // Determine activity trend based on current activity and time since last feed
  useEffect(() => {
    // Find where we are in the activity curve
    const activityProfile = storage === 'room' ? profile.room : profile.fridge;
    
    // If recently fed (less than the time to first peak), trend is up
    const timeToFirstPeak = activityProfile.find(p => p.percentage === 100)?.hours || 0;
    
    if (hoursSinceLastFeed < timeToFirstPeak) {
      setActivityTrend('up');
    } else {
      // Find the plateau threshold (when activity bottoms out)
      const lastPoint = activityProfile[activityProfile.length - 1];
      const plateauThreshold = lastPoint.hours;
      const minActivity = lastPoint.percentage;
      
      if (hoursSinceLastFeed >= plateauThreshold && currentActivity <= minActivity + 5) {
        // We're at the plateau phase
        setActivityTrend('stable');
      } else {
        // We're in the declining phase
        setActivityTrend('down');
      }
    }
  }, [hoursSinceLastFeed, storage, profile, currentActivity]);

  // Render the appropriate trend icon
  const renderTrendIcon = () => {
    switch (activityTrend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-400 ml-1" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-amber-400 ml-1" />;
      case 'stable':
        return <Minus className="w-4 h-4 text-gray-400 ml-1" />;
    }
  };

  return (
    <div className={`py-3 pl-0 pr-6 min-w-[50%] sm:min-w-[200px] ${className}`}>
      <div className="flex items-center mb-5 gap-2">
        <span className="text-sm text-white font-bold">Activity Level </span>
        <div className="flex items-center">
          <span className="text-2xl text-amber-400 font-brand font-light italic">{Math.round(currentActivity)}%</span>
          {renderTrendIcon()}
        </div>
      </div>
      
      <div className="relative h-16 mb-5 ml-6">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between">
          {[100, 50, 0].map((value) => (
            <div 
              key={value} 
              className="w-full ml-4 h-px bg-gray-200"
              style={{ bottom: `${value}%` }}
            />
          ))}
        </div>
        
        {/* Activity bars */}
        <div className="absolute inset-0 flex items-end justify-between px-0.5">
          {points.map((point, index) => {
            const isActive = hoursSinceLastFeed >= point.hours;
            const isCurrent = 
              (index === points.length - 1 && hoursSinceLastFeed > point.hours) ||
              (hoursSinceLastFeed >= point.hours && 
               hoursSinceLastFeed <= point.hours + (points[1].hours - points[0].hours));
            
            return (
              <div 
                key={point.id}
                className="relative flex-1 mx-0.5"
                style={{ height: '100%' }}
              >
                <div 
                  className={`absolute bottom-0 w-full transition-all duration-500 rounded-t ${
                    isCurrent
                      ? 'bg-amber-500'
                      : isActive
                      ? 'bg-amber-400'
                      : 'bg-gray-200'
                  }`}
                  style={{ 
                    height: visibleBars[index] ? `${point.percentage}%` : '0%',
                    opacity: isActive ? 1 : 0.3,
                    transform: `scaleY(${visibleBars[index] ? 1 : 0})`,
                    transformOrigin: 'bottom'
                  }}
                />
                <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] text-gray-200 whitespace-nowrap">
                  {point.label || `${point.hours}h`}
                </span>
              </div>
            );
          })}
        </div>

        {/* Y-axis labels */}
        <div className="absolute -left-5 -mt-1.5 -mb-1.5 inset-y-0 flex flex-col justify-between text-[10px] text-gray-200">
          <span>100%</span>
          <span>50%</span>
          <span>0%</span>
        </div>
      </div>
    </div>
  );
}